import type { CategorizedTransaction, LedgerAccount, LedgerTransaction, LoanTerms, RecurringTransaction } from '@fd/finance-core';
import type {
  AccountInfo,
  AllocationSpec,
  IncomePeriod as EngineIncome,
  SettlementRecord,
  SettlementRule as EngineRule,
  TransactionInput as EngineTransaction,
} from '@fd/settlement-engine';
import type { Account, IncomePeriod, Participant, Settlement, SettlementRule, Transaction } from './entities.ts';

// Bridges between the transport shapes (amounts as text) and the domain engines (bigint).

const toParticipants = (participants: readonly Participant[]) =>
  participants.map((p) => ({ personId: p.personId, value: p.value === null ? null : BigInt(p.value) }));

function allocation(method: Transaction['splitMethod'], participants: readonly Participant[]): AllocationSpec | null {
  return method === null ? null : { method, participants: toParticipants(participants) };
}

export function toLedgerTransaction(transaction: Transaction): LedgerTransaction {
  return {
    id: transaction.id,
    accountId: transaction.accountId,
    bookingDate: transaction.bookingDate,
    amountMinor: BigInt(transaction.amountMinor),
    currency: transaction.currency,
    baseAmountMinor: transaction.baseAmountMinor === null ? null : BigInt(transaction.baseAmountMinor),
    kind: transaction.kind,
    status: transaction.status,
    categoryId: transaction.categoryId,
    counterpartAccountId: transaction.counterpartAccountId,
    splits: transaction.splits.map((s) => ({ amountMinor: BigInt(s.amountMinor), categoryId: s.categoryId })),
  };
}

export function toRecurringTransaction(transaction: Transaction): RecurringTransaction {
  return {
    id: transaction.id,
    accountId: transaction.accountId,
    bookingDate: transaction.bookingDate,
    amountMinor: BigInt(transaction.amountMinor),
    currency: transaction.currency,
    kind: transaction.kind,
    status: transaction.status,
    merchant: transaction.merchantName,
    description: transaction.description ?? transaction.descriptionRaw,
    categoryId: transaction.categoryId,
    counterpartAccountId: transaction.counterpartAccountId,
  };
}

export function toCategorized(transaction: Transaction): CategorizedTransaction {
  return {
    id: transaction.id,
    merchant: transaction.merchantName,
    description: transaction.description ?? transaction.descriptionRaw,
    kind: transaction.kind,
    categoryId: transaction.categoryId,
  };
}

/** Loan terms of a liability account; older records without the fields read as unknown. */
export function toLoanTerms(account: Account): LoanTerms {
  return {
    openingBalanceMinor: BigInt(account.openingBalanceMinor),
    openingDate: account.openingBalanceDate,
    interestRateBasisPoints: account.interestRateBasisPoints ?? null,
    paymentMinor: account.paymentMinor ? BigInt(account.paymentMinor) : null,
    paymentDay: account.paymentDay ?? null,
    originalPrincipalMinor: account.originalPrincipalMinor ? BigInt(account.originalPrincipalMinor) : null,
    paymentCount: account.paymentCount ?? null,
    finalPaymentMinor: account.finalPaymentMinor ? BigInt(account.finalPaymentMinor) : null,
    firstPaymentDate: account.firstPaymentDate ?? null,
  };
}

export function toLedgerAccount(account: Account): LedgerAccount {
  return {
    id: account.id,
    type: account.type,
    currency: account.currency,
    balanceMinor: BigInt(account.balanceMinor),
    isSavingsAsset: account.isSavingsAsset,
    includeInNetWorth: account.includeInNetWorth,
    archivedAt: account.archivedAt,
  };
}

export function toEngineTransaction(transaction: Transaction): EngineTransaction {
  return {
    id: transaction.id,
    accountId: transaction.accountId,
    bookingDate: transaction.bookingDate,
    amountMinor: BigInt(transaction.amountMinor),
    currency: transaction.currency,
    kind: transaction.kind,
    status: transaction.status,
    categoryId: transaction.categoryId,
    expenseType: transaction.expenseType,
    allocation: allocation(transaction.splitMethod, transaction.participants),
    paidByPersonId: transaction.paidByPersonId,
    refundOfTransactionId: transaction.refundOfTransactionId,
    splits: transaction.splits.map((split) => ({
      id: split.id,
      amountMinor: BigInt(split.amountMinor),
      categoryId: split.categoryId,
      expenseType: split.expenseType,
      allocation: allocation(split.splitMethod, split.participants),
    })),
  };
}

export function toAccountInfo(account: Account): AccountInfo {
  return {
    id: account.id,
    ownerPersonIds: account.owners,
    defaultExpenseType: account.defaultExpenseType,
    fundingVersions: account.fundingVersions.map((v) => ({
      effectiveFrom: v.effectiveFrom,
      effectiveTo: v.effectiveTo,
      shares: v.shares.map((s) => ({ personId: s.personId, basisPoints: BigInt(s.basisPoints) })),
    })),
  };
}

export function toEngineRule(rule: SettlementRule): EngineRule {
  return {
    id: rule.id,
    scope: rule.scope,
    scopeId: rule.scopeId,
    versions: rule.versions.map((v) => ({
      effectiveFrom: v.effectiveFrom,
      effectiveTo: v.effectiveTo,
      allocation: { method: v.method, participants: toParticipants(v.participants) },
    })),
  };
}

export function toEngineIncome(income: IncomePeriod): EngineIncome {
  return {
    personId: income.personId,
    amountMinor: BigInt(income.amountMinor),
    currency: income.currency,
    effectiveFrom: income.effectiveFrom,
    effectiveTo: income.effectiveTo,
  };
}

export function toSettlementRecord(settlement: Settlement): SettlementRecord {
  return {
    id: settlement.id,
    status: settlement.status,
    settledOn: settlement.settledOn,
    currency: settlement.currency,
    transfers: settlement.transfers.map((t) => ({
      fromPersonId: t.fromPersonId,
      toPersonId: t.toPersonId,
      amountMinor: BigInt(t.amountMinor),
    })),
  };
}
